import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MarketCalculusCard } from '../../../components/preview/Preview'
import { RSelect } from '../../../components/Component'
import { useGlobalStoreData } from '../../../store/globalStore'
import { RegistryFirmsServices } from '../../../services/McDataServices'
import { useSDSsearchParams } from '../../../store/searchDataStore'
import FirmListServerSide from '../../../mailTemplate/src/components/atomic/organisms/FirmListServerSide'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Tooltip } from '@mui/material'
import '../../../assets/scss/apps/_serverside-table.scss'
import { sortTregFirms } from '../McPageHelpers'

const TradeRegistryTabs = ({ id, dropdownData, tableData, setTableData, selectedOptions, setSelectedOptions, isActive }) => {
  const { t } = useTranslation()

  const searchParams = useSDSsearchParams()

  const languageGSD = useGlobalStoreData.language()

  const [rawData, setRawData] = useState({})
  const [loading, setLoading] = useState(false)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [serverSidePagination, setServerSidePagination] = useState({ pageSize: 10 })

  const requestRef = useRef(null)
  const hasOpenedOnceRef = useRef(false)

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText === '' || searchText.length >= 3) {
        setDebouncedSearchText(searchText)
      }
    }, 800)

    return () => {
      clearTimeout(handler)
    }
  }, [searchText])

  useEffect(() => {
    if (!selectedOptions.length) {
      setTableData(id, [])
      return
    }

    const controller = new AbortController()
    requestRef.current = controller

    const fetchData = async () => {
      try {
        setLoading(true)
        const payload = {
          importerCountryCode: searchParams.countryToExport,
          currentCountryCode: searchParams.yourCountry,
          hsCodes: searchParams.hsCode,
          sectorCodes: selectedOptions.map(opt => opt.code).join(';'),
          page: serverSidePagination.pageIndex ?? 0,
          searchKeyword: debouncedSearchText || undefined,
          ...serverSidePagination
        }
        const response = await RegistryFirmsServices.getFirms(payload, { signal: controller.signal })
        if (!controller.signal.aborted) {
          setRawData(response || [])
          setTableData(id, response?.registryFirms || [])
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Data fetch error:', error)
          setTableData(id, [])
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchData()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, serverSidePagination, debouncedSearchText])

  const handleChange = selected => setSelectedOptions(id, selected || [])

  useEffect(() => {
    if (isActive && !selectedOptions.length && !hasOpenedOnceRef.current) {
      setMenuIsOpen(true)
      hasOpenedOnceRef.current = true
    }
  }, [isActive, selectedOptions.length])

  return (
    <MarketCalculusCard>
      <div className="flex align-items-center mb-3">
        <h5 className="mb-0">{t('mailTemplate.wizard.firmList.sector')}</h5>
        <div style={{ minWidth: 250, paddingInlineStart: 10 }}>
          <RSelect
            id="firmOptions"
            options={sortTregFirms(dropdownData)}
            isMulti
            value={selectedOptions}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            onChange={handleChange}
            getOptionValue={opt => opt.code}
            getOptionLabel={opt => `${opt.code}${opt?.name?.[languageGSD] ? `-${opt.name[languageGSD]}` : ''}`}
            menuIsOpen={isActive ? menuIsOpen : false}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}

            formatOptionLabel={(opt, { context }) => {
              const label = `${opt.code}${opt?.name?.[languageGSD] ? `-${opt.name[languageGSD]}` : ''}`

              if (context === 'value') return opt.code

              return (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                  <span
                    style={{
                      flex: 1,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      fontWeight: opt.relevant ? 700 : 400,
                      lineHeight: 1.3
                    }}
                  >
                    {label}
                  </span>
                  {opt.relevant && (
                    <Tooltip title={t('mailTemplate.campaignReview.relevant')} placement="right">
                      <CheckCircleOutlineIcon fontSize="small" color="success" style={{ marginLeft: 8 }}/>
                    </Tooltip>
                  )}
                </div>
              )
            }}

            styles={{
              option: (base, state) => ({
                ...base,
                display: 'flex',
                alignItems: 'flex-start',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                lineHeight: 1.3,
                fontWeight: state.data?.relevant ? 700 : 400
              }),
              menu: base => ({
                ...base,
                maxWidth: 420
              }),
              menuList: base => ({
                ...base,
                maxHeight: 320
              }),
              menuPortal: base => ({
                ...base,
                zIndex: 1300
              })
            }}

            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div style={{ paddingInlineStart: 10 }}>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder={t('table_search_by_firm_name_or_profile')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={!selectedOptions?.length}
            style={{ width: 300 }}
          />
        </div>
      </div>
      <div className="my-3">
        <FirmListServerSide
          setServerSidePaginationByKey={setServerSidePagination}
          serverSidePagination={serverSidePagination}
          data={tableData || []}
          rowCount={rawData?.totalRecordCount || 0}
          fetching={loading}
          languageGSD={languageGSD}
          className="serverside-table"
        />
      </div>
    </MarketCalculusCard>
  )
}

export default TradeRegistryTabs
